import { LIVE_CHUNK_SIZE } from '@racemap/utilities/consts/events';
import { type FC, useCallback, useEffect } from 'react';
import { RenderInfoWorker } from '../ChunkManager';
import { getPointsBuffer } from '../model';
import { parseChunks } from '../parse';
import { getEventById } from '../statistics';
import { useStore } from '../store';
import { ceilTime, floorTime } from '../utils';
import { correctedNow } from '../utils/clock';
import { useLoop } from '../utils/hooks';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;

function LiveMotionManager({ eventId }: { eventId: string }) {
  const endTime = useStore((state) => getEventById(state, eventId).get().endTime);
  const netReplay = useStore((state) => getEventById(state, eventId).get().modules.netReplay);
  const pollingInterval = useStore(
    (state) => getEventById(state, eventId).get().options.pollingInterval,
  );
  const chunkManager = useStore(
    (state) => state.eventsChangeable.find((ev) => ev.eventId === eventId)?.chunkManager,
  );

  const handleDownload = useCallback(
    async function handleDownload() {
      if (chunkManager == null || endTime <= correctedNow()) return;
      // add() trims the initial range around a live chunk only if that range is already there
      await chunkManager.ready;
      const now = correctedNow();

      const rangeStart = new Date(floorTime(now, LIVE_CHUNK_SIZE));
      const rangeEnd = new Date(ceilTime(now + 1, LIVE_CHUNK_SIZE));
      const bufOpt = await getPointsBuffer(CDN_HOST, eventId, {
        chunkSize: LIVE_CHUNK_SIZE,
        range: [rangeStart, rangeEnd],
        netReplay,
      });
      const chunks = bufOpt.map(parseChunks).getOrElse(() => []);
      for (const chunk of chunks) {
        chunkManager.add(chunk);
      }
    },
    [endTime, eventId, netReplay, chunkManager],
  );

  useLoop(handleDownload, pollingInterval);

  useEffect(() => {
    return () => {
      void RenderInfoWorker.disposeDecoder(eventId);
    };
  }, [eventId]);

  return null;
}

export const GroupLiveMotionManager: FC = () => {
  const events = useStore((state) => state.events);
  return (
    <>
      {events.map((event) => (
        <LiveMotionManager key={event.eventId} eventId={event.eventId} />
      ))}
    </>
  );
};
